import React from 'react';
import { createPortal } from 'react-dom';
import { Search, Plus, Filter, Settings, LayoutGrid, List, Calendar, Download, X, Upload, RefreshCw, Columns, Table, ChevronDown, HelpCircle } from 'lucide-react';
import { Icon } from '@iconify/react';
import HelpPage from '../HelpPage/HelpPage';
import { useLocation } from 'react-router-dom';
import { useNavigationContext } from '../../../context/NavigationContext';
import './SubHeader.css';
import API_ENDPOINTS from '../../../config/apiEndpoints';

/**
 * Common SubHeader Component
 * Used across CRM modules to provide a consistent header interface.
 */
const SubHeader = ({
    title = '',
    searchQuery = '',
    setSearchQuery = () => { },
    searchPlaceholder = 'Search...',
    viewMode = 'table',
    setViewMode = () => { },
    onAddClick = null,
    onFilterClick = null,
    onExportClick = null,
    onSettingsClick = null,
    onRefreshClick = null,
    onColumnsClick = null,
    onImportClick = null,
    onCustomExportClick = null,
    showAdd = true,
    showFilter = true,
    showExport = false,
    showCustomExport = false,
    showImport = false,
    showSettings = false,
    showViewSwitcher = true,
    showGrid = true,
    showTable = true,
    showCalendar = true,
    showRefresh = false,
    showColumns = false,
    showAddInMenu = false,
    massActionPanel = null,
    totalCount = null,
    customMenuItems = null,
    advanceFilterFields = [],
    onAdvanceFilterSubmit = null,
    showCustomSearch = false,
    onCustomSearchSubmit = null,
    onCustomSearchClear = null,
    customSearchFetcher = null,
    filterCount = 0,
    showAdvanceFilter = true,
    showSearch = true,
    showExpandableSearch = false,
    isSearchExpanded = false,
    setIsSearchExpanded = () => { },
    onSearchClick = null,
    children = null,
    leftContent = null,
    centerContent = null,
    customPadding = null,
    customHeight = null,
    className = '',
    beforeAddContent = null,
    pageName = null,
    pageCode = null,
    showHelpPanel = true
}) => {

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && onSearchClick) {
            onSearchClick();
        }
    };

    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isScrolled, setIsScrolled] = React.useState(false);

    React.useEffect(() => {
        const handleScroll = (e) => {
            // Check both window scroll and element-level scroll (for internal containers)
            const scrollTop = e.target === document ? window.scrollY : (e.target.scrollTop || 0);
            if (scrollTop > 20) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        // Also listen for bubbling scroll events in case of sub-pane scrolling
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, []);
    const [isAdvanceFilterOpen, setIsAdvanceFilterOpen] = React.useState(false);
    const [applyLogic, setApplyLogic] = React.useState('ANY');
    const [allChecked, setAllChecked] = React.useState(false);
    const [activeFilters, setActiveFilters] = React.useState([]);
    const [currentRow, setCurrentRow] = React.useState({ field: '', operator: 'contains', value: '' });
    const [isCustomSearchOpen, setIsCustomSearchOpen] = React.useState(false);
    const [customSearchOptions, setCustomSearchOptions] = React.useState([]);
    const [selectedCustomSearch, setSelectedCustomSearch] = React.useState(null);
    const [isCustomSearchLoading, setIsCustomSearchLoading] = React.useState(false);
    
    // Help Page State
    const [isHelpOpen, setIsHelpOpen] = React.useState(false);
    const [showHelpTooltip, setShowHelpTooltip] = React.useState(false);

    React.useEffect(() => {
        // Tooltip will now only be shown on hover
        // if (pageName && showHelpPanel) {
        //     setShowHelpTooltip(true);
        // }
    }, [pageName, showHelpPanel]);

    const handleOpenHelp = () => {
        setIsHelpOpen(true);
        if (showHelpTooltip) {
            setShowHelpTooltip(false);
        }
    };

    // Global Permission Check based on current route
    const location = useLocation();
    const navigationContext = useNavigationContext();
    const routePerms = navigationContext?.getRoutePermissions ? navigationContext.getRoutePermissions(location.pathname) : null;

    const effectiveShowAdvanceFilter = routePerms && routePerms.Search === false ? false : showAdvanceFilter;
    const effectiveShowSearch = routePerms && routePerms.Search === false ? false : showSearch;
    const effectiveShowFilter = routePerms && routePerms.Search === false ? false : showFilter;

    const getPageCode = React.useCallback(() => {
        return pageCode || routePerms?.ModuleCode || routePerms?.MenuCode || pageName;
    }, [pageCode, routePerms, pageName]);

    const { setCurrentPageCode } = navigationContext || {};
    React.useEffect(() => {
        if (setCurrentPageCode) {
            const code = getPageCode();
            setCurrentPageCode(code || "");
        }
    }, [getPageCode, setCurrentPageCode]);

    // If massActionPanel is provided (e.g., when items are selected), it takes precedence
    if (massActionPanel) {
        return (
            <header className={`premium-subheader mass-action-active ${isScrolled ? 'is-scrolled' : ''}`}>
                {massActionPanel}
            </header>
        );
    }

    const toggleExpansion = () => setIsExpanded(!isExpanded);

    const toggleAdvanceFilter = () => {
        setIsAdvanceFilterOpen(!isAdvanceFilterOpen);
        if (isExpanded) setIsExpanded(false);
    };
    const API_BASE_AZURE = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
    const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
    const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const GET_CUSTOME_SEARCH = `${NEWV3_BASE_URL}${API_ENDPOINTS.COMMON.GET_LEAD_CUSTOME_SEARCH}`;
    const Get_EntityPlaceHolder = `${NEWV3_BASE_URL}${API_ENDPOINTS.COMMON.GET_ENTITY_PLACEHOLDER_FOR_CUSTOM_EXPORT || '/UserCRM/FuncToGetEntityPlaceHolder'}`;
    const Get_Export_Setting = `${API_BASE_AZURE}${API_ENDPOINTS.COMMON.EXPORT_DATA_SETTING}`;
    const Export_Widget_Download = `${API_BASE_AZURE}${API_ENDPOINTS.COMMON.EXPORT_WIDGET_DOWNLOAD}`;




    const handleApply = () => {
        let finalFilters = [...activeFilters];
        if (currentRow.field && currentRow.value) {
            // Check if it's already added to prevent duplicates
            const isAlreadyAdded = activeFilters.some(f =>
                f.field === currentRow.field &&
                f.operator === currentRow.operator &&
                f.value === currentRow.value
            );
            if (!isAlreadyAdded) {
                const newFilter = { ...currentRow, id: Date.now() };
                finalFilters = [...finalFilters, newFilter];
                setActiveFilters(finalFilters);
                setCurrentRow({ field: '', operator: 'contains', value: '' });
            }
        }

        onAdvanceFilterSubmit({
            logic: applyLogic,
            rows: finalFilters,
            allChecked
        });
        setIsAdvanceFilterOpen(false);
    };

    const handleReset = () => {
        setActiveFilters([]);
        setCurrentRow({ field: '', operator: 'contains', value: '' });
        setAllChecked(false);
        onAdvanceFilterSubmit(null);
    };

    const addCurrentToActive = () => {
        if (!currentRow.field || !currentRow.value) return;
        const newFilters = [...activeFilters, { ...currentRow, id: Date.now() }];
        setActiveFilters(newFilters);
        setCurrentRow({ field: '', operator: 'contains', value: '' });
    };

    const removeActiveFilter = (id) => {
        const newFilters = activeFilters.filter(f => f.id !== id);
        setActiveFilters(newFilters);
        // Apply filter immediately in background
        onAdvanceFilterSubmit({
            logic: applyLogic,
            rows: newFilters,
            allChecked
        });
    };
    const fetchCustomSearchOptions = async () => {
        if (customSearchOptions.length > 0) return;
        setIsCustomSearchLoading(true);
        try {
            if (typeof customSearchFetcher === 'function') {
                // Caller-injected fetcher (e.g. WCF getLeadCustomSearch)
                const data = await customSearchFetcher();
                setCustomSearchOptions(Array.isArray(data) ? data : []);
            } else {
                // Default pipeline-based fetch
                const uid = localStorage.getItem('userId') || '34594';
                const NEWV3_BASE_URL_INTERNAL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
                const Custome_Search_URL = `${NEWV3_BASE_URL_INTERNAL}${API_ENDPOINTS.COMMON.GET_LEAD_CUSTOME_SEARCH}`;

                const payload = {
                    Token: "-2295521862261168",
                    Details: { userId: parseInt(uid, 10) }
                };

                const response = await fetch(Custome_Search_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (response.ok) {
                    const result = await response.json();
                    const data = result?.Details || result?.d || [];
                    setCustomSearchOptions(Array.isArray(data) ? data : []);
                }
            }
        } catch (error) {
            console.error("Error fetching custom search:", error);
        } finally {
            setIsCustomSearchLoading(false);
        }
    };

    const fetchCustomExportPlaceholder = async () => {
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const entityName = pageName || "Task"; // Fallback to Task or use pageName (Lead, Enquiry, Appointment)

            const payload = {
                Token: "-2295521862261168",
                Details: {
                    strEntityName: entityName,
                    strParentID: uid
                }
            };

            const response = await fetch(Get_EntityPlaceHolder, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();
                console.log("Custom Export Placeholder Data:", result);
                // Implementation for displaying fields can go here
            }
        } catch (error) {
            console.error("Error fetching custom export placeholder:", error);
        }
    };

    const toggleCustomSearch = () => {
        if (!isCustomSearchOpen) {
            fetchCustomSearchOptions();
            setIsAdvanceFilterOpen(false);
            if (isExpanded) setIsExpanded(false);
        }
        setIsCustomSearchOpen(!isCustomSearchOpen);
    };

    const handleCustomSearchSubmit = () => {
        if (onCustomSearchSubmit && selectedCustomSearch) {
            // Pass the CriteriaName as the search name (matches legacy SearchName field)
            onCustomSearchSubmit(selectedCustomSearch.CriteriaName || selectedCustomSearch.Text || selectedCustomSearch.Name || selectedCustomSearch);
        }
    };

    const handleCustomSearchClear = () => {
        setSelectedCustomSearch(null);
        if (onCustomSearchClear) {
            onCustomSearchClear();
        }
        setIsCustomSearchOpen(false);
    };

    const renderCustomSearchPanel = () => (
        <div className="custom-search-panel-overlay">
            <div className="custom-search-panel">
                <div className="custom-search-header">
                    <div className="header-title">
                        <Icon icon="mdi:filter-outline" className="header-icon" />
                        <span>Custom Search</span>
                    </div>
                    <button className="custom-search-close-btn" onClick={() => setIsCustomSearchOpen(false)} title="Close">
                        <X size={18} />
                    </button>
                </div>
                <div className="custom-search-body">
                    <div className="custom-search-dropdown-wrapper">
                        <select
                            className="custom-search-select"
                            value={selectedCustomSearch?.ID || ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                const found = customSearchOptions.find(opt =>
                                    String(opt.ID ?? opt.Id ?? '') === val
                                );
                                setSelectedCustomSearch(found || null);
                            }}
                        >
                            <option value="">-- Select Custom Search --</option>
                            {isCustomSearchLoading && (
                                <option disabled>Loading...</option>
                            )}
                            {customSearchOptions.map((opt, idx) => (
                                <option key={opt.ID ?? opt.Id ?? idx} value={opt.ID ?? opt.Id ?? ''}>
                                    {opt.CriteriaName || opt.Text || opt.Name}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={16} className="dropdown-chevron" />
                    </div>
                </div>
                <div className="custom-search-footer">
                    <button
                        className="custom-search-clear-btn"
                        onClick={handleCustomSearchClear}
                    >
                        Clear
                    </button>
                    <button
                        className="custom-search-apply-btn"
                        onClick={handleCustomSearchSubmit}
                        disabled={!selectedCustomSearch}
                    >
                        Search
                    </button>
                </div>
            </div>
        </div>
    );

    const renderAdvanceFilterPanel = () => (
        <div className="advance-filter-overlay">
            <div className="advance-filter-modal">
                <div className="filter-modal-header">
                    <h2 className="modal-title">Filter</h2>
                    <X size={20} className="close-modal-icon" onClick={() => setIsAdvanceFilterOpen(false)} />
                </div>

                <div className="filter-modal-body">
                    {/* All Task Checkbox */}
                    <div className="all-task-section">
                        <label className="checkbox-container">
                            <input
                                type="checkbox"
                                checked={allChecked}
                                onChange={(e) => setAllChecked(e.target.checked)}
                            />
                            <span className="checkmark"></span>
                            <span className="checkbox-label">All {(typeof title === 'string' ? title.replace(/s$/, '') : 'Record')}</span>
                        </label>
                    </div>

                    {/* Add Row Section */}
                    <div className="add-filter-row">
                        <select
                            className="modal-select field-select"
                            value={currentRow.field}
                            onChange={(e) => {
                                const newField = e.target.value;
                                let newOp = currentRow.operator || 'contains';

                                // Auto-set operator for specific fields
                                if (newField === 'FollowupStatus') {
                                    newOp = 'equals';
                                } else if (advanceFilterFields.find(f => f.value === newField)?.type === 'date') {
                                    newOp = 'contains';
                                }

                                setCurrentRow({ ...currentRow, field: newField, operator: newOp });
                            }}
                        >
                            <option value="">--- Select Field ---</option>
                            {advanceFilterFields.map(f => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                        </select>

                        {currentRow.field !== 'FollowupStatus' && (
                            <select
                                className="modal-select operator-select"
                                value={currentRow.operator}
                                onChange={(e) => setCurrentRow({ ...currentRow, operator: e.target.value })}
                            >
                                <option value="">--- Operator ---</option>
                                {advanceFilterFields.find(f => f.value === currentRow.field)?.type !== 'date' && (
                                    <option value="equals">Equals</option>
                                )}
                                <option value="contains">Contains</option>
                                <option value="not_contains">Does not contain</option>
                                <option value="not_equal">Not Equal</option>
                                <option value="starts_with">Starts With</option>
                                <option value="ends_with">Ends With</option>
                            </select>
                        )}

                        <div className="modal-input-wrapper">
                            {advanceFilterFields.find(f => f.value === currentRow.field)?.type === 'date' ? (
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                    <input
                                        type="date"
                                        className="modal-text-input"
                                        value={currentRow.value}
                                        onChange={(e) => setCurrentRow({ ...currentRow, value: e.target.value })}
                                        style={{ width: '100%' }}
                                    />
                                    <span style={{ fontSize: '10px', color: '#666', marginTop: '2px', marginLeft: '4px' }}>
                                        Format: DD/MM/YYYY
                                    </span>
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    className="modal-text-input"
                                    placeholder="Search value"
                                    value={currentRow.value}
                                    onChange={(e) => setCurrentRow({ ...currentRow, value: e.target.value })}
                                />
                            )}
                        </div>
                        <button className="modal-add-row-btn-premium" onClick={addCurrentToActive} title="Add Filter">
                            <Plus size={20} />
                        </button>
                    </div>

                    {/* Logic Toggle Section */}
                    <div className="modal-logic-section">
                        <label className="radio-container">
                            <input
                                type="radio"
                                name="modalApplyLogic"
                                checked={applyLogic === 'ALL'}
                                onChange={() => setApplyLogic('ALL')}
                            />
                            <span className="radio-mark"></span>
                            <span className="radio-label">Apply to All</span>
                        </label>
                        <label className="radio-container">
                            <input
                                type="radio"
                                name="modalApplyLogic"
                                checked={applyLogic === 'ANY'}
                                onChange={() => setApplyLogic('ANY')}
                            />
                            <span className="radio-mark"></span>
                            <span className="radio-label">Apply Any One</span>
                        </label>
                    </div>

                    {/* Active Filters List */}
                    <div className="active-filters-list">
                        {activeFilters.map((filter) => (
                            <div key={filter.id} className="active-filter-item">
                                <span className="item-field">{advanceFilterFields.find(f => f.value === filter.field)?.label}</span>
                                <span className="item-operator">{filter.operator.replace('_', ' ').charAt(0).toUpperCase() + filter.operator.replace('_', ' ').slice(1)}</span>
                                <span className="item-value">{filter.value}</span>
                                <button className="item-remove-btn" onClick={() => removeActiveFilter(filter.id)} title="Remove Filter">
                                    —
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="filter-modal-footer">
                    <button className="modal-reset-btn" onClick={handleReset}>Clear All</button>
                    <button className="modal-apply-btn" onClick={handleApply}>Apply</button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {createPortal(
                <HelpPage 
                    isOpen={isHelpOpen} 
                    onClose={() => setIsHelpOpen(false)} 
                    pageName={pageName}
                    pageCode={getPageCode()} 
                    themeColor="#0f172a" 
                />,
                document.body
            )}
            {isAdvanceFilterOpen && createPortal(renderAdvanceFilterPanel(), document.body)}
            {isCustomSearchOpen && createPortal(renderCustomSearchPanel(), document.body)}
            <header
                className={`premium-subheader ${isScrolled ? 'is-scrolled' : ''} ${className}`}
                style={{
                    ...(customPadding ? { padding: customPadding } : {}),
                    ...(customHeight ? { height: customHeight } : {})
                }}
            >
                {/* Left Section: Title */}
                <div className="subheader-title-section">
                    <h1>
                        {title}
                        {totalCount !== null && totalCount !== undefined && !!(
                            searchQuery?.trim() ||
                            filterCount > 0 ||
                            (activeFilters && activeFilters.filter(f => f && f !== 'All').length > 0)
                        ) && (
                                <span className="subheader-title-count">
                                    ( {totalCount} matching records )
                                </span>
                            )}
                    </h1>
                    {leftContent}
                </div>

                {/* Middle Section: Search Bar or Custom Content */}
                <div className="subheader-center-section">
                    {centerContent ? centerContent : (effectiveShowSearch && !showExpandableSearch && (
                        <div id="help-target-search-bar" className="subheader-search-wrapper">
                            <Search
                                size={20}
                                className="subheader-search-icon themed-icon cursor-pointer"
                                onClick={() => onSearchClick && onSearchClick()}
                            />
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="subheader-search-input"
                            />
                        </div>
                    ))}
                </div>

                {/* Right Section: Controls */}
                <div className="subheader-right-section">
                    {showExpandableSearch && (
                        <div id="help-target-search-bar" className={`expandable-search-container ${isSearchExpanded || searchQuery ? 'expanded' : ''}`}>
                            <button
                                className="search-icon-btn"
                                onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                                title="Search"
                            >
                                <Search size={20} className="themed-icon" />
                            </button>
                            <input
                                type="text"
                                className="expandable-input"
                                placeholder={searchPlaceholder}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                autoFocus={isSearchExpanded}
                            />
                            {isSearchExpanded && searchQuery && (
                                <button className="search-clear-btn" onClick={() => setSearchQuery('')}>
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    )}

                    {/* Module-specific extra controls */}
                    {children}

                    {/* View Switcher */}
                    {showViewSwitcher && (
                        <div className="subheader-view-switcher">
                            {showGrid && (
                                <button
                                    title="Grid View"
                                    className={`view-switch-btn ${viewMode === 'grid' ? 'active' : ''}`}
                                    onClick={() => setViewMode('grid')}
                                >
                                    <LayoutGrid size={18} className="view-switch-icon" />
                                </button>
                            )}
                            {showTable && (
                                <button
                                    title="List View"
                                    className={`view-switch-btn ${viewMode === 'table' ? 'active' : ''}`}
                                    onClick={() => setViewMode('table')}
                                >
                                    <Table size={18} className="view-switch-icon" />
                                </button>
                            )}
                            {showCalendar && (
                                <button
                                    title="Calendar View"
                                    className={`view-switch-btn ${viewMode === 'calendar' ? 'active' : ''}`}
                                    onClick={() => setViewMode('calendar')}
                                >
                                    <Calendar size={18} className="view-switch-icon" />
                                </button>
                            )}
                        </div>
                    )}

                    {/* Main Action Buttons */}
                    <div id="help-target-actions" className="subheader-actions">
                    {/* Button(s) placed just before the + add button */}
                        {beforeAddContent}

                        {showAdd && onAddClick && !showAddInMenu && (
                            <button
                                className="subheader-action-btn primary-add"
                                onClick={onAddClick}
                                title="Add New"
                            >
                                <Plus size={20} />
                            </button>
                        )}

                        <div className={`subheader-expandable-group ${isExpanded ? 'expanded' : ''}`}>
                            {isExpanded ? (
                                <div className="expanded-action-bar">
                                    <button className="menu-action-btn" onClick={toggleExpansion} title="Close">
                                        <X size={20} />
                                    </button>
                                    <div className="menu-divider"></div>

                                    {customMenuItems}
                                    {customMenuItems && <div className="menu-divider"></div>}

                                    {showAdd && onAddClick && showAddInMenu && (
                                        <>
                                            <button className="menu-action-btn highlight" onClick={onAddClick} title="Add">
                                                <Plus size={24} />
                                            </button>
                                            <div className="menu-divider"></div>
                                        </>
                                    )}

                                    {showColumns && onColumnsClick && (
                                        <>
                                            <button className="menu-action-btn" onClick={onColumnsClick} title="Column Config">
                                                <Columns size={20} />
                                            </button>
                                            <div className="menu-divider"></div>
                                        </>
                                    )}

                                    {showRefresh && onRefreshClick && (
                                        <>
                                            <button className="menu-action-btn" onClick={onRefreshClick} title="Refresh">
                                                <RefreshCw size={20} />
                                            </button>
                                            <div className="menu-divider"></div>
                                        </>
                                    )}

                                    {effectiveShowFilter && onFilterClick && (
                                        <>
                                            <button className="menu-action-btn filter-btn-with-count" onClick={onFilterClick} title="Filter">
                                                <Filter size={20} />
                                                {filterCount > 0 && (
                                                    <span className="filter-badge-count">{filterCount}</span>
                                                )}
                                            </button>
                                            <div className="menu-divider"></div>
                                        </>
                                    )}

                                    {showExport && onExportClick && (
                                        <>
                                            <button className="menu-action-btn" onClick={onExportClick} title="Export">
                                                <Upload size={20} />
                                            </button>
                                            <div className="menu-divider"></div>
                                        </>
                                    )}

                                    {showCustomExport && onCustomExportClick && (
                                        <>
                                            <button className="menu-action-btn" onClick={onCustomExportClick} title="Custom Export">
                                                <Icon icon="mdi:export-variant" fontSize={22} style={{ color: '#c2410c' }} />
                                            </button>
                                            <div className="menu-divider"></div>
                                        </>
                                    )}

                                    {showImport && onImportClick && (
                                        <>
                                            <button className="menu-action-btn" onClick={onImportClick} title="Import">
                                                <Download size={20} />
                                            </button>
                                            <div className="menu-divider"></div>
                                        </>
                                    )}

                                    {showCustomSearch && (
                                        <>
                                            <button
                                                className={`menu-action-btn ${isCustomSearchOpen ? 'active' : ''}`}
                                                onClick={toggleCustomSearch}
                                                title="Custom Search"
                                            >
                                                <Icon icon="mdi:filter-plus-outline" fontSize={22} />
                                            </button>
                                            <div className="menu-divider"></div>
                                        </>
                                    )}

                                    {showSettings && effectiveShowAdvanceFilter && (
                                        <button className="menu-action-btn" onClick={toggleAdvanceFilter} title="Advance Filter">
                                            <Filter size={20} />
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="subheader-collapsed-actions">
                                    {showSettings && (
                                        <button
                                            className="subheader-action-btn"
                                            onClick={toggleExpansion}
                                            title="More Actions"
                                        >
                                            <Settings size={20} />
                                        </button>
                                    )}
                                    {pageName && showHelpPanel && (
                                        <div 
                                            style={{ position: 'relative', display: 'inline-block' }}
                                            onMouseEnter={() => setShowHelpTooltip(true)}
                                            onMouseLeave={() => setShowHelpTooltip(false)}
                                        >
                                            <button
                                                className="subheader-action-btn"
                                                onClick={handleOpenHelp}
                                                title="Help"
                                            >
                                                <HelpCircle size={20} />
                                            </button>
                                            {showHelpTooltip && (
                                                <div className="help-tooltip-pulse">
                                                    Need Help?
                                                    <div className="tooltip-arrow"></div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Module-specific extra controls */}
                </div>
            </header>

            {/* Active Filter Badges Row */}
            {!!(activeFilters && activeFilters.length > 0) && (
                <div className="subheader-filter-badges-row">
                    {/* Individual Filter Chips removed to declutter as per request. Summary and Clear All remain. */}

                    <div style={{ flex: 1 }}></div>

                    <button className="modal-reset-btn" onClick={handleReset} style={{ padding: '4px 12px' }}>
                        Clear All
                    </button>

                    {totalCount !== null && totalCount !== undefined && (
                        <div className="subheader-filter-summary-badge" style={{ marginLeft: '12px' }}>
                            <span className="summary-label">Matches:</span>
                            <span className="summary-value">{totalCount}</span>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default SubHeader;
